import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import L from 'leaflet';

export const PROPERTY_GROUP_VIEW_MODES = {
  COMBINED: 'combined',
  SPLIT: 'split',
};

const VIEW_MODE_STORAGE_KEY = 'siteTakeoffV3.operationalPropertyGroupViewMode';

const getInitialViewMode = () => {
  try {
    const stored = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (stored === PROPERTY_GROUP_VIEW_MODES.SPLIT) return stored;
  } catch (_) {}
  return PROPERTY_GROUP_VIEW_MODES.COMBINED;
};

const ensureFeatureGroup = (map, ref) => {
  let group = ref.current;
  if (!group || !map.hasLayer(group)) {
    group = new L.FeatureGroup();
    map.addLayer(group);
    ref.current = group;
  }
  return group;
};

const formatSquareFeet = value => Math.round(Number(value || 0)).toLocaleString();

const addGeoJsonLayer = (group, geometry, style, tooltip) => {
  if (!geometry) return;
  const layer = L.geoJSON({ type: 'Feature', properties: {}, geometry }, {
    style: () => style,
    interactive: true,
  });
  if (tooltip) layer.bindTooltip(tooltip);
  group.addLayer(layer);
};

export default function useOperationalPropertyGroup({
  url,
  jobsite,
  mapRef,
  visible = true,
  currentPropertyLinesVisible = false,
  redrawKey,
  onProjectedAreasChanged,
  onMemberBoundariesChanged,
}) {
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewModeState] = useState(getInitialViewMode);
  const boundaryLayerRef = useRef(null);
  const sliceLayerRef = useRef(null);
  const areaLayerRef = useRef(null);
  const onProjectedAreasChangedRef = useRef(onProjectedAreasChanged);
  const onMemberBoundariesChangedRef = useRef(onMemberBoundariesChanged);

  useEffect(() => {
    onProjectedAreasChangedRef.current = onProjectedAreasChanged;
  }, [onProjectedAreasChanged]);

  useEffect(() => {
    onMemberBoundariesChangedRef.current = onMemberBoundariesChanged;
  }, [onMemberBoundariesChanged]);

  const notifyProjectedAreasChanged = useCallback((payload) => {
    const ids = (payload?.projectedJobSiteIds || []).map(String);
    if (jobsite?._id && ids.includes(String(jobsite._id))) {
      onProjectedAreasChangedRef.current?.(payload);
    }
  }, [jobsite]);

  const loadGroups = useCallback(async () => {
    if (!url || !jobsite?._id) {
      setGroups([]);
      setActiveGroupId(null);
      setWorkspace(null);
      return [];
    }
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        `${url}/api/customer/operational-property-groups/for-jobsite/${jobsite._id}`
      );
      const nextGroups = response.data?.success && Array.isArray(response.data.groups)
        ? response.data.groups
        : [];
      setGroups(nextGroups);
      setActiveGroupId(previous => (
        nextGroups.some(group => group._id === previous)
          ? previous
          : nextGroups[0]?._id || null
      ));
      if (!nextGroups.length) setWorkspace(null);
      return nextGroups;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load property groups');
      setGroups([]);
      setWorkspace(null);
      return [];
    } finally {
      setLoading(false);
    }
  }, [url, jobsite]);

  const loadWorkspace = useCallback(async (groupId = activeGroupId) => {
    if (!url || !groupId) {
      setWorkspace(null);
      return null;
    }
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(
        `${url}/api/customer/operational-property-groups/${groupId}/workspace`
      );
      if (!response.data?.success) return null;
      setWorkspace(response.data);
      notifyProjectedAreasChanged(response.data);
      return response.data;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load group workspace');
      setWorkspace(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [url, activeGroupId, notifyProjectedAreasChanged]);

  useEffect(() => { loadGroups(); }, [loadGroups]);
  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const setViewMode = useCallback((nextMode) => {
    const normalized = nextMode === PROPERTY_GROUP_VIEW_MODES.SPLIT
      ? PROPERTY_GROUP_VIEW_MODES.SPLIT
      : PROPERTY_GROUP_VIEW_MODES.COMBINED;
    setViewModeState(normalized);
    try { window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, normalized); } catch (_) {}
  }, []);

  useEffect(() => {
    return () => {
      const map = mapRef.current;
      [boundaryLayerRef, sliceLayerRef, areaLayerRef].forEach(ref => {
        if (map && ref.current) {
          try { map.removeLayer(ref.current); } catch (_) {}
        }
        ref.current = null;
      });
    };
  }, [mapRef]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const boundariesGroup = ensureFeatureGroup(map, boundaryLayerRef);
    const slicesGroup = ensureFeatureGroup(map, sliceLayerRef);
    const areasGroup = ensureFeatureGroup(map, areaLayerRef);
    boundariesGroup.clearLayers();
    slicesGroup.clearLayers();
    areasGroup.clearLayers();
    if (!visible || !workspace) return;

    const membersById = new Map(
      (workspace.group?.members || []).map(member => [String(member.jobSiteId), member])
    );

    if (viewMode === PROPERTY_GROUP_VIEW_MODES.SPLIT) {
      (workspace.slices || []).forEach(slice => {
        const status = slice.allocationStatus;
        if (
          status === 'included'
          && String(slice.jobSiteId || '') === String(jobsite?._id || '')
          && slice.projectedAreaIds?.length
        ) return;
        const style = status === 'unallocated'
          ? { color: '#d32f2f', weight: 2, dashArray: '8, 5', fillColor: '#ef5350', fillOpacity: 0.22 }
          : status === 'excluded'
            ? { color: '#757575', weight: 1.5, dashArray: '4, 5', fillColor: '#bdbdbd', fillOpacity: 0.16 }
            : { color: '#1976d2', weight: 1.5, fillColor: '#42a5f5', fillOpacity: 0.16 };
        const label = status === 'unallocated'
          ? `Unallocated · ${formatSquareFeet(slice.squareFeet)} SF`
          : `${slice.propertyName || membersById.get(String(slice.jobSiteId))?.propertyName || 'Property'} · ${status} · ${formatSquareFeet(slice.squareFeet)} SF`;
        addGeoJsonLayer(slicesGroup, slice.geometry, style, label);
      });

      (workspace.boundaries || []).forEach(boundary => {
        if (
          currentPropertyLinesVisible &&
          String(boundary.jobSiteId || '') === String(jobsite?._id || '')
        ) return;
        const member = membersById.get(String(boundary.jobSiteId));
        addGeoJsonLayer(
          boundariesGroup,
          boundary.geometry,
          { color: '#1565c0', weight: 2.5, dashArray: '7, 5', fillOpacity: 0 },
          `${member?.propertyName || 'Group property'} · ${boundary.source} boundary`
        );
      });
    }

    (workspace.areas || []).forEach(area => {
      const combined = viewMode === PROPERTY_GROUP_VIEW_MODES.COMBINED;
      addGeoJsonLayer(
        areasGroup,
        area.geometry,
        {
          color: area.style?.color || '#f28c00',
          weight: combined ? area.style?.weight || 4 : 2,
          opacity: combined ? area.style?.opacity ?? 0.95 : 0.7,
          fillColor: area.style?.fillColor || '#ffd200',
          fillOpacity: combined ? 0.12 : 0,
          dashArray: combined ? null : '7, 5',
        },
        `${area.name || 'Group service area'} · ${combined ? 'combined group area' : 'shared area outline'}`
      );
    });
  }, [
    workspace,
    visible,
    currentPropertyLinesVisible,
    mapRef,
    redrawKey,
    jobsite,
    viewMode,
  ]);

  const mutate = useCallback(async (request) => {
    setSaving(true);
    setError('');
    try {
      const response = await request();
      if (!response.data?.success) throw new Error(response.data?.message || 'Request failed');
      notifyProjectedAreasChanged(response.data);
      await loadGroups();
      const nextGroupId = response.data.group?._id || activeGroupId;
      if (nextGroupId) {
        setActiveGroupId(nextGroupId);
        await loadWorkspace(nextGroupId);
      }
      return response.data;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Request failed');
      return null;
    } finally {
      setSaving(false);
    }
  }, [activeGroupId, loadGroups, loadWorkspace, notifyProjectedAreasChanged]);

  const createGroup = useCallback((name) => {
    if (!jobsite?._id) return Promise.resolve(null);
    return mutate(() => axios.post(
      `${url}/api/customer/operational-property-groups`,
      { name: name || 'Property group', memberJobSiteIds: [jobsite._id] }
    ));
  }, [url, jobsite, mutate]);

  const updateMembers = useCallback((memberJobSiteIds) => {
    if (!activeGroupId) return Promise.resolve(null);
    return mutate(() => axios.put(
      `${url}/api/customer/operational-property-groups/${activeGroupId}`,
      { memberJobSiteIds }
    ));
  }, [url, activeGroupId, mutate]);

  const addMember = useCallback((candidate) => {
    const currentIds = (workspace?.group?.members || []).map(member => String(member.jobSiteId));
    if (!candidate?._id || currentIds.includes(String(candidate._id))) return Promise.resolve(null);
    return updateMembers([...currentIds, candidate._id]);
  }, [workspace, updateMembers]);

  const removeMember = useCallback((jobSiteId) => {
    const nextIds = (workspace?.group?.members || [])
      .map(member => String(member.jobSiteId))
      .filter(id => id !== String(jobSiteId));
    if (!nextIds.length) return Promise.resolve(null);
    return updateMembers(nextIds);
  }, [workspace, updateMembers]);

  const createArea = useCallback((paths, name, details = {}) => {
    if (!activeGroupId) return Promise.resolve(null);
    return mutate(() => axios.post(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/areas`,
      { paths, name: name || 'Group service area', ...details }
    ));
  }, [url, activeGroupId, mutate]);

  const loadImportableAreas = useCallback(async () => {
    if (!url || !activeGroupId) return [];
    try {
      setError('');
      const response = await axios.get(
        `${url}/api/customer/operational-property-groups/${activeGroupId}/importable-areas`
      );
      return response.data?.success && Array.isArray(response.data.areas)
        ? response.data.areas
        : [];
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError.message || 'Unable to load existing areas');
      return [];
    }
  }, [url, activeGroupId]);

  const importAreas = useCallback((areaIds) => {
    if (!activeGroupId || !areaIds?.length) return Promise.resolve(null);
    return mutate(() => axios.post(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/import-areas`,
      { areaIds }
    ));
  }, [url, activeGroupId, mutate]);

  const saveMemberBoundaries = useCallback(async (boundaries) => {
    if (!activeGroupId || !boundaries?.length) return null;
    const result = await mutate(() => axios.put(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/member-boundaries`,
      { boundaries }
    ));
    if (result) onMemberBoundariesChangedRef.current?.(result);
    return result;
  }, [url, activeGroupId, mutate]);

  const updateArea = useCallback((areaId, updates) => {
    if (!activeGroupId || !areaId) return Promise.resolve(null);
    return mutate(() => axios.put(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/areas/${areaId}`,
      updates
    ));
  }, [url, activeGroupId, mutate]);

  const recalculateArea = useCallback((areaId) => {
    if (!activeGroupId || !areaId) return Promise.resolve(null);
    return mutate(() => axios.post(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/areas/${areaId}/recalculate`
    ));
  }, [url, activeGroupId, mutate]);

  const deleteArea = useCallback((areaId) => {
    if (!activeGroupId || !areaId) return Promise.resolve(null);
    return mutate(() => axios.delete(
      `${url}/api/customer/operational-property-groups/${activeGroupId}/areas/${areaId}`
    ));
  }, [url, activeGroupId, mutate]);

  const searchJobsites = useCallback(async (query) => {
    if (!url || !String(query || '').trim()) return [];
    try {
      const response = await axios.get(`${url}/api/customer/jobsites/search`, {
        params: { q: String(query).trim() }
      });
      return (response.data?.jobsites || []).filter(site => site.source === 'jobsite' && site._id);
    } catch (_) {
      return [];
    }
  }, [url]);

  const fitToGroup = useCallback(() => {
    const map = mapRef.current;
    const boundaryLayers = boundaryLayerRef.current;
    const areaLayers = areaLayerRef.current;
    const layers = boundaryLayers?.getLayers().length ? boundaryLayers : areaLayers;
    if (!map || !layers || !layers.getLayers().length) return;
    const bounds = layers.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds.pad(0.12), { maxZoom: 19 });
  }, [mapRef]);

  return {
    groups,
    activeGroupId,
    setActiveGroupId,
    viewMode,
    setViewMode,
    workspace,
    group: workspace?.group || groups.find(group => group._id === activeGroupId) || null,
    loading,
    saving,
    error,
    createGroup,
    addMember,
    removeMember,
    createArea,
    loadImportableAreas,
    importAreas,
    saveMemberBoundaries,
    updateArea,
    updateAreaPaths: (areaId, paths) => updateArea(areaId, { paths }),
    recalculateArea,
    deleteArea,
    searchJobsites,
    fitToGroup,
    reload: loadWorkspace,
  };
}
